import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useSaveToYourAccount from '@app/hooks/use-save-to-your-account';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import DPO from 'dpo8core';
import { inject } from '@app/providers/QAU.ContextProvider';
import { setFooterConfig } from '@app/actions/footerActions';
import { CUSTOMIZE_VIEW, LOGIN_SIGNUP_VIEW } from '@app/constants';
import { navigateToView } from '@app/router';
import { stateEditor, resetCustomizeData } from '@app/actions/customizeActions';
import { getAuthorizationStatus } from '@app/actions/authActions';
import {
  setWhiteInkOption,
  setTolerance,
  stateArtworkPreview,
  setAdvancedModeVisible,
  setTouchUpMode,
  setLastClickCoordinates,
  resetWhiteInkOptionsData,
} from '@app/actions/whiteInkOptionsActions';
import { startLoading, endLoading } from '@app/actions/spinnerActions';
import { processFinishedArtwork, finishArtwork } from '@app/actions/artworkDataActions';

import WhiteInkOptionsPanel from './white-ink-options-panel/WhiteInkOptionsPanel';
import TopToolPanel from './top-tool-panel/TopToolPanel';
import WorkingArea from './working-area/WorkingArea';
import useImageProcessor from '@app/hooks/use-image-processor';
import useTimeMachine from '@app/hooks/use-time-machine';
import { getWhiteTreatmentHint, parseWhiteTreatmentHint, getOriginalNameFromHint } from '@app/utils/hints';
import { isSaveUploadArtworkToMyAccount } from '@app/utils/utils';
import './WhiteInkOptionsView.scss';

const WhiteInkOptionsView = ({
  flow,
  imageField,
  artworkPreview,
  nextDisabled,
  includesWhiteUnderprint,
  lastClickCoordinates,
  context,
  flowConfig,
  processFinishedArtwork,
  finishArtwork,
  setFooterConfig,
  startLoading,
  endLoading,
  whiteInkOption,
  setWhiteInkOption,
  tolerance,
  setTolerance,
  stateEditor,
  stateArtworkPreview,
  setAdvancedModeVisible,
  setTouchUpMode,
  setLastClickCoordinates,
  resetCustomizeData,
  resetWhiteInkOptionsData,
  matrix,
  getAuthorizationStatus,
  isAuthorized,
  saveToYourAccount,
}) => {
  const [originalUrl, setOriginalUrl] = useState(null);
  const [originalCmykUrl, setOriginalCmykUrl] = useState(null);
  const [originalImageData, setOriginalImageData] = useState(null);
  const [originalTolerance, setOriginalTolerance] = useState(null);
  const [imageFilter, setImageFilter] = useState(null);
  const [imageParams, setImageParams] = useState(null);
  const [actionName, setActionName] = useState(null);
  const [imageFieldId, setImageFieldId] = useState(null);
  const [imageDataToProcess, setImageDataToProcess] = useState(null);
  const [advancedModeImageData, setAdvancedModeImageData] = useState(null);
  const [whiteInkOptionsImageData, setWhiteInkOptionsImageData] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [silent, setSilent] = useState(false);
  const { processedImageData, processing } = useImageProcessor(imageDataToProcess, imageFilter, filterType);
  const analyzer = useMemo(() => new DPO.project.ImageAnalyzer(context, flowConfig), [context, flowConfig]);
  const [imageGenerating, setImageGenerating] = useState(false);
  const {
    currentState: currentTimeImageData,
    storeState: storeInTimeMachine,
    canUndo,
    canRedo,
    undo,
    redo,
    reset: resetTimeMachine,
  } = useTimeMachine(null, 5);

  const generateOriginalImageData = useCallback(() => {
    if (!originalUrl || !originalCmykUrl) return;

    analyzer.getImageBySrc(originalUrl).then(img => {
      const rgbImageData = analyzer.getImageDataForImage(img);

      setImageParams(params => ({
        ...params,
        naturalWidth: rgbImageData.width,
        naturalHeight: rgbImageData.height,
      }));
      analyzer.getImageBySrc(originalCmykUrl).then(img => {
        const cmykImageData = analyzer.getImageDataForImage(img);
        setOriginalImageData({ rgb: rgbImageData, cmyk: cmykImageData });
        generateFieldImageData();
      });
    });
  }, [analyzer, originalUrl, originalCmykUrl]);

  const generateFieldImageData = useCallback(() => {
    const { storage } = context;

    analyzer.getImageBySrc(storage.urlForField(imageField, 'low')).then(img => {
      const rgbImageData = analyzer.getImageDataForImage(img);
      analyzer.getImageBySrc(storage.urlForField(imageField, 'cmyk')).then(img => {
        const cmykImageData = analyzer.getImageDataForImage(img);
        setWhiteInkOptionsImageData({ rgb: rgbImageData, cmyk: cmykImageData });

        whiteInkOption !== 'removeAllWhite' && setImageDataToProcess({ rgb: rgbImageData, cmyk: cmykImageData });
        setSilent(false);
      });
    });
  }, []);

  const requestImage = (name, type) => {
    return new Promise((resolve, reject) => {
      const { storage } = context;

      function imageGenerated(httpStorage, imageName, imageType, data) {
        context.storage.un('image:generated', imageGenerated, this);
        imageType === type && resolve(data.uri);
      }

      context.storage.on('image:generated', imageGenerated, this);
      storage.urlForImage(name, type, true);
    });
  };

  const setOriginalImages = async (originalName, originalUrl, originalCmykUrl) => {
    originalUrl ? setOriginalUrl(originalUrl) : setOriginalUrl(await requestImage(originalName, 'low'));
    originalCmykUrl ? setOriginalCmykUrl(originalCmykUrl) : setOriginalCmykUrl(await requestImage(originalName, 'cmyk'));
  };

  const setWhiteOptionsFromHint = useCallback(() => {
    startLoading();
    const { storage } = context;
    const { hints } = imageField;
    let { source: originalName } = imageField;
    const whiteTreatmentHint = getWhiteTreatmentHint(hints);
    const originalNameFromHint = getOriginalNameFromHint(hints);
    if (originalNameFromHint) {
      originalName = originalNameFromHint.value;
    }
    let originalUrl = storage.lowImgMap[originalName]?.uri;
    let originalCmykUrl = storage.cmykImgMap[originalName]?.uri;

    setSilent(true);
    setOriginalImages(originalName, originalUrl, originalCmykUrl);

    const { whiteTreatment, tolerance } = parseWhiteTreatmentHint(whiteTreatmentHint);

    let whiteInkOptionFromHint;

    switch (whiteTreatment) {
      case 'keep':
        whiteInkOptionFromHint = 'keepAllWhite';
        break;
      case 'background':
        whiteInkOptionFromHint = 'removeWhiteBackground';
        break;
      case 'remove':
        whiteInkOptionFromHint = 'removeAllWhite';
        break;
      case 'selective':
        whiteInkOptionFromHint = 'selectWhiteToRemove';
        break;
      default:
        whiteInkOptionFromHint = null;
    }

    whiteInkOptionFromHint && setWhiteInkOption(whiteInkOptionFromHint);

    if (tolerance) {
      setOriginalTolerance(+tolerance);
      setTolerance(+tolerance);
    }

    setImageParams(params => ({
      ...params,
      originalName,
      keepOriginal: true,
      replaceSource: true,
      replaceBackground: true,
      saveToMyAccount: false,
      hintList: hints.filter(({ name }) => name === 'removedBackgroundImg'),
      whiteTreatment: whiteTreatment || null,
    }));
    setImageFieldId(imageField.id);
    endLoading();
  }, [startLoading, context, imageField, setWhiteInkOption, setTolerance, setSilent, endLoading]);

  const getNaturalImageCoordinates = params => {
    const { x, y, width, height } = params;
    const { naturalWidth, naturalHeight } = imageParams;

    const naturalX = Math.ceil(x * (naturalWidth / width));
    const naturalY = Math.ceil(y * (naturalHeight / height));

    return [naturalX, naturalY];
  };

  const resetAllChanges = () => {
    setImageDataToProcess(originalImageData);
    resetTimeMachine(originalImageData);
  }

  useEffect(() => {
    if (!context) return;

    const onProcess = () => setImageGenerating(true);
    const onImageGenerated = () => setImageGenerating(false);

    context.storage.on('image:progress', onProcess, this);
    context.storage.on('image:generated', onImageGenerated, this)

    return () => {
      context.storage.un('image:progress', onProcess, this);
      context.storage.un('image:generated', onImageGenerated, this);
    }
  }, [context]);

  useEffect(() => {
    const filterType = whiteInkOption !== 'selectWhiteToRemove' ? whiteInkOption : null;

    setLastClickCoordinates(null);
    setFilterType(filterType);
    whiteInkOption === 'selectWhiteToRemove' && setWhiteInkOptionsImageData(originalImageData);
  }, [whiteInkOption]);

  useEffect(() => {
    setWhiteInkOptionsImageData(currentTimeImageData);
    setImageDataToProcess(currentTimeImageData);
  }, [currentTimeImageData]);

  useEffect(() => {
    !silent && setWhiteInkOptionsImageData(processedImageData);
    if (!whiteInkOption || whiteInkOption !== 'selectWhiteToRemove') return;

      !currentTimeImageData && resetTimeMachine(imageDataToProcess);
      lastClickCoordinates && storeInTimeMachine(processedImageData);
      lastClickCoordinates && setImageDataToProcess(processedImageData);
  }, [processedImageData]);

  useEffect(() => {
    setImageDataToProcess(originalImageData);
  }, [originalImageData]);

  useEffect(() => {
    setImageDataToProcess(advancedModeImageData);
    setWhiteInkOptionsImageData(advancedModeImageData);
  }, [advancedModeImageData]);

  useEffect(() => {
    processing ? startLoading() : endLoading();
  }, [endLoading, processing, startLoading]);

  useEffect(() => {
    if (tolerance && !originalTolerance) {
      setOriginalTolerance(tolerance);
    }
  }, [tolerance]);

  useEffect(() => {
    if (whiteInkOption === 'selectWhiteToRemove') return;
    const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');

    setImageFilter({
      minWhiteRgbSum: context.getMinWhiteRgbSum(tolerance),
      minSingleChannel: context.getMinSingleChannel(tolerance),
      featheringRadius: +removeWhiteFeature.getParamValue('featheringRadius'),
    });
  }, [context, flowConfig, tolerance, whiteInkOption]);

  const { finishArtworkProject } = useSaveToYourAccount({
    flow,
    context,
    flowConfig,
    finishArtwork,
    processFinishedArtwork,
    isAuthorized,
    matrix,
    getAuthorizationStatus,
    setFooterConfig
  });

  const storeChanges = useCallback(storeImage => {
    return new Promise(resolve => {
      startLoading();
      const updateProject = () => {
        const onProjectUpdated = () => {
          flow.un('project:updated', onProjectUpdated, this);
          flow.un('images:changed', updateProject, this);
          endLoading();
          resolve();
        };

        flow.updateProject();
        flow.on('project:updated', onProjectUpdated, this);
      };

      if (storeImage) {
        flow.chain.performAction([imageField], actionName, imageParams);
        flow.on('images:changed', updateProject, this);
      } else {
        updateProject();
      }
    });
  }, [actionName, endLoading, flow, imageField, imageParams, startLoading]);

  const doFinishArtwork = useCallback((saveToYourAccount) => {
    storeChanges(includesWhiteUnderprint && actionName).then(() => finishArtworkProject(saveToYourAccount));
  }, [storeChanges, includesWhiteUnderprint, finishArtworkProject, actionName]);

  const updateBlendingMode = useCallback(() => {
    const paperMaterial = context.getPaperMaterial();
    const blendingMode = paperMaterial.blendingColor ? 'material' : 'removeWhite';

    context.blendingMode = includesWhiteUnderprint ? null : blendingMode;
    artworkPreview && artworkPreview.update();
  }, [includesWhiteUnderprint, context, artworkPreview]);

  const navigateToCustomize = useCallback(
    (projectLocation, productId) => {
      setTouchUpMode(null);
      setAdvancedModeVisible(false);
      setLastClickCoordinates(null);
      setActionName(null);
      resetCustomizeData();
      stateEditor(null);
      flow.editor = null;
      navigateToView(CUSTOMIZE_VIEW, projectLocation, productId);
      resetWhiteInkOptionsData();
      stateArtworkPreview(null);
    },
    [
      flow.editor,
      resetCustomizeData,
      resetWhiteInkOptionsData,
      setAdvancedModeVisible,
      setLastClickCoordinates,
      setTouchUpMode,
      stateArtworkPreview,
      stateEditor,
    ],
  );

  const saveHintAndNavigate = useCallback(() => {
    const {
      projectData: {
        product: { id: productId },
        projectLocation,
      },
    } = context;
    storeChanges(includesWhiteUnderprint && actionName).then(() => navigateToCustomize(projectLocation, productId));
  }, [actionName, context, includesWhiteUnderprint, navigateToCustomize, storeChanges]);

  const updateImageOnPreview = useCallback((imageData) => {
    if (!imageData) return;
    const { cmyk: cmykImageData } = imageData;
    const { storage, selector } = context;
    const field = selector.getField(imageField.id);
    const imgBase64 = analyzer.imageDataToBase64(cmykImageData);

    storage.redefineUrl(field, imgBase64);
    artworkPreview.updateFields([field]);
  }, [analyzer, artworkPreview, context, imageField.id]);

  const resetUrlRedefinition = useCallback(() => {
    const { storage, selector } = context;
    const field = selector.getField(imageField.id);

    storage.resetUrlRedefinition();

    artworkPreview.updateFields([field]);
  }, [artworkPreview, context, imageField.id]);

  useEffect(() => {
    updateBlendingMode();
  }, [artworkPreview, updateBlendingMode]);

  useEffect(() => {
    setFooterConfig({
      visible: true,
      backHidden: processing || imageGenerating || !artworkPreview,
      nextDisabled,
      onBack: saveHintAndNavigate,
      onNext: () => doFinishArtwork(saveToYourAccount),
      nextLocKey: 'qau-finish-btn',
      noteMessageLocKey: '',
      showSaveToMyAccount: isSaveUploadArtworkToMyAccount(flowConfig),
    });
  }, [nextDisabled, doFinishArtwork, setFooterConfig, imageGenerating, matrix.view, saveToYourAccount]);

  useEffect(() => {
    flow && setWhiteOptionsFromHint();
  }, [flow, setWhiteOptionsFromHint]);

  useEffect(() => {
    analyzer && originalUrl && originalCmykUrl && generateOriginalImageData();
  }, [analyzer, originalUrl, originalCmykUrl, generateOriginalImageData]);

  useEffect(() => {
    if (!imageParams) return;

    storeChanges(imageParams.whiteTreatment && !includesWhiteUnderprint && actionName).then(updateBlendingMode);
  }, [includesWhiteUnderprint]);

  useEffect(() => {
    if (!whiteInkOptionsImageData || !artworkPreview || silent) return;
    const imageData = includesWhiteUnderprint ? whiteInkOptionsImageData : originalImageData;

    let imageBlob;
    let whiteTreatment;
    let isWhiteRemoved = false;

    switch (whiteInkOption) {
      case 'keepAllWhite':
        whiteTreatment = 'keep';
        break;
      case 'removeAllWhite':
        whiteTreatment = 'remove-' + tolerance;
        isWhiteRemoved = true;
        break;
      case 'removeWhiteBackground':
        whiteTreatment = 'background-' + tolerance;
        isWhiteRemoved = true;
        break;
      case 'selectWhiteToRemove':
        whiteTreatment = 'selective';
        isWhiteRemoved = true;
        break;
      default:
        whiteTreatment = null;
    }

    if (isWhiteRemoved) {
      imageBlob = analyzer.imageDataToBlob(imageData.rgb);
    }

    const imageDataForPreview = isWhiteRemoved ? whiteInkOptionsImageData : originalImageData;

    includesWhiteUnderprint ? updateImageOnPreview(imageDataForPreview) : resetUrlRedefinition();

    setImageParams(params => ({
      ...params,
      whiteTreatment,
      url: isWhiteRemoved ? null : originalUrl,
      keepOriginal: isWhiteRemoved,
      files: isWhiteRemoved ? [imageBlob] : [],
    }));
    setActionName(isWhiteRemoved ? 'createImageFile' : 'createImageURL');
  }, [
    whiteInkOptionsImageData,
    whiteInkOption,
    analyzer,
    includesWhiteUnderprint,
    context,
    originalImageData,
    imageFieldId,
    artworkPreview,
    originalUrl,
  ]);

  useEffect(() => {
    if (whiteInkOption !== 'selectWhiteToRemove' || !lastClickCoordinates || !whiteInkOptionsImageData) return;
    const maxRGBSum = 765; // 255 + 255 + 255
    const pixelCoordinates = getNaturalImageCoordinates(lastClickCoordinates);
    const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');
    const clickWhiteToRemoveTolerance = +removeWhiteFeature.getParamValue('clickWhiteToRemoveTolerance');
    const minSingleChannel = +removeWhiteFeature.getParamValue('minSingleChannel');
    const featheringRadius = +removeWhiteFeature.getParamValue('featheringRadius');
    const isPixelInWhiteRange = analyzer.isPixelInWhiteRange(whiteInkOptionsImageData.rgb, pixelCoordinates);

    if (!isPixelInWhiteRange) return;

    const rgbSum = analyzer.getPixelRGBSum(whiteInkOptionsImageData.rgb, pixelCoordinates);
    const whiteRgbSumAdjustment = maxRGBSum - context.getMinWhiteRgbSum(clickWhiteToRemoveTolerance);
    const minWhiteRgbSum = rgbSum - whiteRgbSumAdjustment;

    setImageFilter({
      minWhiteRgbSum,
      minSingleChannel,
      featheringRadius,
      coordinates: pixelCoordinates,
      originalImageData,
      toColor: [0, 0, 0, 0],
    });

    setFilterType('FillColor');
  }, [whiteInkOption, lastClickCoordinates]);

  useEffect(() => {
    getAuthorizationStatus();
  }, [getAuthorizationStatus]);

  useEffect(() => {
    const prevView = sessionStorage.getItem('prevView');
    if (prevView === LOGIN_SIGNUP_VIEW && saveToYourAccount && context.projectTransformer) {
      setFooterConfig({ backLocKey: 'qau-back-btn', nextHidden: false });
      doFinishArtwork(isAuthorized);
      sessionStorage.setItem('prevView', '');
    }
  }, [context.projectTransformer, doFinishArtwork, isAuthorized, saveToYourAccount, setFooterConfig]);

  return (
    <div id="qau-white-options-view">
      <WhiteInkOptionsPanel
        resetAllChanges={resetAllChanges}
        advancedModeProps={{
          originalImageData,
          whiteInkOptionsImageData,
          advancedModeImageData,
          setAdvancedModeImageData,
          getNaturalImageCoordinates,
          updateImageOnPreview,
        }}
        resetButtonDisabled={!canUndo}
      />
      <TopToolPanel undoRedoProps={{
        canUndo: includesWhiteUnderprint && canUndo,
        canRedo: includesWhiteUnderprint && canRedo,
        undo,
        redo,
        }}
      />
      <WorkingArea />
    </div>
  );
};

const mapStateToProps = (
  {
    whiteInkOptions: { includesWhiteUnderprint, lastClickCoordinates, nextDisabled, artworkPreview, whiteInkOption, tolerance },
    matrix,
    auth: { isAuthorized },
    footer: { saveToYourAccount },
  },
  { flow },
) => {
  const {
    editor: {
      selectedFields: [image],
    },
  } = flow;

  return {
    imageField: image,
    artworkPreview,
    nextDisabled,
    includesWhiteUnderprint,
    lastClickCoordinates,
    whiteInkOption,
    tolerance,
    matrix,
    isAuthorized,
    saveToYourAccount,
  };
};

const mapDispatchToProps = {
  processFinishedArtwork,
  finishArtwork,
  setFooterConfig,
  startLoading,
  endLoading,
  setWhiteInkOption,
  setTolerance,
  stateEditor,
  resetCustomizeData,
  resetWhiteInkOptionsData,
  stateArtworkPreview,
  setAdvancedModeVisible,
  setTouchUpMode,
  setLastClickCoordinates,
  getAuthorizationStatus,
};

WhiteInkOptionsView.propTypes = {
  flow: PropTypes.object.isRequired,
  imageField: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  nextDisabled: PropTypes.bool.isRequired,
  includesWhiteUnderprint: PropTypes.bool,
  lastClickCoordinates: PropTypes.object,
  flowConfig: PropTypes.object.isRequired,
  whiteInkOption: PropTypes.string,
  finishArtwork: PropTypes.func.isRequired,
  processFinishedArtwork: PropTypes.func.isRequired,
  setWhiteInkOption: PropTypes.func.isRequired,
  stateEditor: PropTypes.func.isRequired,
  resetCustomizeData: PropTypes.func.isRequired,
  resetWhiteInkOptionsData: PropTypes.func.isRequired,
  stateArtworkPreview: PropTypes.func.isRequired,
  setLastClickCoordinates: PropTypes.func.isRequired,
  matrix: PropTypes.object,
  getAuthorizationStatus: PropTypes.func.isRequired,
  isAuthorized: PropTypes.bool.isRequired,
  saveToYourAccount: PropTypes.bool.isRequired,
};

export default inject('flow', 'flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(WhiteInkOptionsView));
